import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import { clearToken, isAuthenticated } from "../../services/api";
import { getCurrentUser, type UserInfo } from "../../services/auth";
import { hasPermission } from "../../utils/permissions";
import RIcon from "../../components/RIcon";

const ROUTE_PERMISSION_RULES: Array<{ prefix: string; code: string }> = [
  { prefix: "/workbench/admin/users", code: "users:read" },
  { prefix: "/workbench/admin/roles", code: "roles:read" },
  { prefix: "/workbench/admin/departments", code: "departments:read" },
  { prefix: "/workbench/admin/jobs", code: "job_types:read" },
  { prefix: "/workbench/admin/job-stages", code: "work_stages:read" },
  { prefix: "/workbench/admin/menus", code: "menus:read" },
  { prefix: "/workbench/admin/data-permissions", code: "data_permissions:read" },
  { prefix: "/workbench/files", code: "files:read" },
  { prefix: "/workbench/projects", code: "projects:read" },
  { prefix: "/workbench/content-sub", code: "subscriptions:read" },
  { prefix: "/workbench/dashboard", code: "files:read" },
  { prefix: "/workbench/ai-chat", code: "files:read" },
  { prefix: "/workbench/doc-gen", code: "files:read" },
  { prefix: "/workbench/model-training", code: "training:read" },
];

function getRequiredPermission(pathname: string): string | null {
  const matched = ROUTE_PERMISSION_RULES
    .filter((item) => pathname === item.prefix || pathname.startsWith(`${item.prefix}/`))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return matched?.code || null;
}

export default function WorkbenchLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { replace: true });
      return;
    }

    getCurrentUser()
      .then((data) => setUser(data))
      .catch(() => {
        clearToken();
        navigate("/login", { replace: true });
      })
      .finally(() => {
        setLoadingUser(false);
      });
  }, [navigate]);

  if (!isAuthenticated()) return null;

  const requiredPermission = getRequiredPermission(location.pathname);
  const denied = !!user && !!requiredPermission && !hasPermission(user, requiredPermission);

  return (
    <div className="min-h-screen bg-[#F4F8FB] flex">
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onToggle={() => setCollapsed(!collapsed)}
        user={user}
      />

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          collapsed ? "lg:ml-16" : "lg:ml-56"
        }`}
      >
        <TopBar
          collapsed={collapsed}
          onToggleSidebar={() => setCollapsed(!collapsed)}
          onMobileMenu={() => setMobileOpen(true)}
          user={user}
        />

        <main className="flex-1 overflow-auto p-4 md:p-6">
          {loadingUser ? (
            <div className="flex items-center justify-center py-20">
              <RIcon name="loader-4-line" className="animate-spin text-2xl text-[#0EA5A0]" />
              <span className="ml-2 text-[#718096]">加载用户信息...</span>
            </div>
          ) : denied ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 text-red-500 flex items-center justify-center">
                <RIcon name="shield-keyhole-line" className="text-2xl" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-[#1A2B3C]">你没有相关权限，请联系管理员</h2>
              <p className="mt-2 text-sm text-[#718096]">当前页面需要权限：{requiredPermission}</p>
            </div>
          ) : (
            <Outlet context={{ user }} />
          )}
        </main>
      </div>
    </div>
  );
}
